import { MEDICAL_REASSURANCE } from "../config/reassurance";

export const EMAIL_SLIDE_CODE = 12;

export const sortQuizSlidesByPosition = (quizData = []) =>
  [...quizData].sort((a, b) => (a?.[0]?.positionCode || 0) - (b?.[0]?.positionCode || 0));

export const normalizeQuizDefinition = (quizData = []) => {
  const sortedSlides = sortQuizSlidesByPosition(quizData);
  const slides = sortedSlides.map((rawSlide) => {
    const meta = rawSlide?.[0] || {};
    return {
      slideCode: meta.code ?? null,
      positionCode: meta.positionCode ?? null,
      isMandatory:
        meta.isMendatory === undefined
          ? true
          : meta.isMendatory === 1 || meta.isMendatory === true || meta.isMendatory === '1',
      rawSlide,
    };
  });

  const slidesByPosition = new Map();
  const rawSlidesByPosition = new Map();
  slides.forEach((slide) => {
    if (slide.positionCode != null) {
      slidesByPosition.set(slide.positionCode, slide);
      rawSlidesByPosition.set(slide.positionCode, slide.rawSlide);
    }
  });

  return {
    sortedSlides,
    slides,
    slidesByPosition,
    rawSlidesByPosition,
    slideCodeMap: buildSlideCodeMap(sortedSlides),
  };
};

// Builds maps between slide codes and position codes
export const buildSlideCodeMap = (quizData) => {
  const codeToPosition = new Map();
  const positionToCode = new Map();
  
  quizData.forEach((slide) => {
    const slideCode = slide[0]?.code;
    const positionCode = slide[0]?.positionCode;
    
    if (slideCode !== undefined && positionCode !== undefined) {
      codeToPosition.set(slideCode, positionCode);
      positionToCode.set(positionCode, slideCode);
    }
  });
  
  return { codeToPosition, positionToCode };
};

// Gets the slide code from a position code
export const getSlideCodeFromPosition = (positionToCodeMap, positionCode) => {
  return positionToCodeMap.get(positionCode);
};

// Gets the position code from a slide code
export const getPositionFromSlideCode = (codeToPositionMap, slideCode) => {
  return codeToPositionMap.get(slideCode);
};

// Finds the next active slide in the navigation
export const getNextActiveSlide = (currentPositionCode, slideNavigation) => {
  // Get all position codes sorted in ascending order
  const sortedPositions = Object.keys(slideNavigation)
    .map(Number)
    .sort((a, b) => a - b);
  
  // Find current position in sorted array  
  const currentIndex = sortedPositions.indexOf(currentPositionCode);
  
  // Look for next active slide
  for (let i = currentIndex + 1; i < sortedPositions.length; i++) {
    const posCode = sortedPositions[i];
    if (slideNavigation[posCode]?.active === true) {
      return posCode;
    }
  }
  
  return undefined;
};

// Finds the previous active slide in the navigation
export const getPreviousActiveSlide = (currentPositionCode, slideNavigation) => {
  // Get all position codes sorted in ascending order
  const sortedPositions = Object.keys(slideNavigation)
    .map(Number)
    .sort((a, b) => a - b);
  
  const currentIndex = sortedPositions.indexOf(currentPositionCode);
  
  for (let i = currentIndex - 1; i >= 0; i--) {
    const posCode = sortedPositions[i];
    if (slideNavigation[posCode]?.active === true) {
      return posCode;
    }
  }
  
  return undefined;
};

// Initializes slide navigation from isMendatory: mandatory slides start active,
// optional slides start inactive. Flow is determined by positionCode order.
// Backward compat: if isMendatory is missing, treat slide as mandatory (active).
export const initializeSlideNavigation = (quizData) => {
  const navigation = {};

  quizData.forEach((slide) => {
    const positionCode = slide[0]?.positionCode;
    const isMandatory = slide[0]?.isMendatory;
    const active =
      isMandatory === undefined
        ? true
        : isMandatory === 1 || isMandatory === true;

    if (positionCode !== undefined) {
      navigation[positionCode] = { active };
    }
  });

  // Ensure first slide is always active so the quiz can start
  const firstPositionCode = Math.min(...Object.keys(navigation).map(Number));
  if (navigation[firstPositionCode]) {
    navigation[firstPositionCode].active = true;
  }

  return navigation;
};

// Email slide code in the quiz config 
export function getEmailForIncompleteSave(slideValues) {
  if (!slideValues || typeof slideValues !== 'object') return '';

  // New format: keyed by slide code
  const fromCode = slideValues[EMAIL_SLIDE_CODE]?.email?.value;
  if (fromCode && typeof fromCode === 'string') return fromCode.trim();

  // Legacy format: keyed by 0-based index (email slide was often index 11)
  const fromIndex11 = slideValues[11]?.email?.value;
  if (fromIndex11 && typeof fromIndex11 === 'string') return fromIndex11.trim();

  // Fallback: scan any key that has an email field (handles other legacy keying)
  for (const slideData of Object.values(slideValues)) {
    if (slideData?.email?.value && typeof slideData.email.value === 'string') {
      return slideData.email.value.trim();
    }
  }
  // DOM fallback when state may not have flushed (e.g. just left email slide)
  if (typeof document !== 'undefined') {
    const input = document.getElementById('email');
    const domEmail = input?.value?.trim();
    if (domEmail) return domEmail;
  }
  return '';
}

// Detects if saved slide values use legacy 0-based index keys (so we normalize to slide-code keys).
// Current format uses slide codes (1, 12, 37, ...); legacy uses indices (0, 1, 2, ...).
function isLegacySlideValuesKeying(savedData) {
  if (!savedData || typeof savedData !== 'object') return false;
  const keys = Object.keys(savedData);
  if (keys.length === 0) return false;
  const numericKeys = keys.map(Number);
  const hasZero = numericKeys.some((k) => k === 0);
  if (!hasZero) return false;
  return numericKeys.every((k) => !Number.isNaN(k) && k >= 0 && Number.isInteger(k));
}

// Normalizes saved slide values to slide-code keying.
// Legacy payloads used 0-based index as keys; current format uses slide codes.
export function normalizeSavedSlideValues(savedData, orderedQuizSlides) {
  if (!savedData || typeof savedData !== 'object') return {};
  if (!Array.isArray(orderedQuizSlides) || orderedQuizSlides.length === 0) return savedData;

  if (!isLegacySlideValuesKeying(savedData)) return savedData;

  const normalized = {};
  orderedQuizSlides.forEach((slide, index) => {
    const slideCode = slide[0]?.code;
    const legacyValue = savedData[index] ?? savedData[String(index)];
    if (slideCode !== undefined && legacyValue != null && typeof legacyValue === 'object') {
      normalized[slideCode] = legacyValue;
    }
  });
  return normalized;
}

// Normalizes saved currentSlide to position code.
// Legacy payloads used 0-based index; current format uses position code (1-based).
export function normalizeSavedPositionCode(savedCurrentSlide, orderedQuizSlides, isLegacyFormat = false) {
  if (savedCurrentSlide == null) return 1;
  const num = Number(savedCurrentSlide);
  if (Number.isNaN(num)) return 1;

  if (isLegacyFormat && Array.isArray(orderedQuizSlides) && orderedQuizSlides.length > 0) {
    if (num >= 0 && num < orderedQuizSlides.length) {
      const positionCode = orderedQuizSlides[num][0]?.positionCode;
      return positionCode ?? num + 1;
    }
  }
  return num;
}

// Normalizes saved slideNavigation to position-code-keyed object.
// Legacy payloads used an array; current format uses object keyed by position code.
// If currentPositionCode is provided, slides after it that are mandatory (isMendatory) are forced
// active so old saves work with the new isMandatory-based flow.
export function normalizeSavedSlideNavigation(savedNavigation, orderedQuizSlides, currentPositionCode) {
  if (!Array.isArray(orderedQuizSlides) || orderedQuizSlides.length === 0) {
    return typeof savedNavigation === 'object' && savedNavigation !== null ? savedNavigation : {};
  }

  let normalized;
  if (Array.isArray(savedNavigation)) {
    normalized = {};
    savedNavigation.forEach((item, index) => {
      const slide = orderedQuizSlides[index];
      const positionCode = slide?.[0]?.positionCode;
      if (positionCode !== undefined) {
        normalized[positionCode] = typeof item === 'object' && item !== null ? { ...item } : { active: !!item };
      }
    });
  } else {
    normalized = typeof savedNavigation === 'object' && savedNavigation !== null ? { ...savedNavigation } : {};
  }

  // For old saves: ensure mandatory slides after current position are active so Next works
  if (currentPositionCode != null && Number(currentPositionCode) === currentPositionCode) {
    const current = Number(currentPositionCode);
    orderedQuizSlides.forEach((slide) => {
      const positionCode = slide[0]?.positionCode;
      const isMandatory = slide[0]?.isMendatory;
      const active =
        isMandatory === undefined ? true : isMandatory === 1 || isMandatory === true || isMandatory === '1';
      if (positionCode != null && positionCode > current && active) {
        normalized[positionCode] = { ...(normalized[positionCode] || {}), active: true };
      }
    });
  }

  return normalized;
}

export function buildPrefillSlideValues(questions, prefill) {
  const out = {};
  if (!Array.isArray(questions) || !prefill || typeof prefill !== 'object') return out;

  const nameVal =
    typeof prefill.name === 'string' ? prefill.name.trim() : prefill.name != null ? String(prefill.name).trim() : '';
  const emailVal =
    typeof prefill.email === 'string'
      ? prefill.email.trim()
      : prefill.email != null
        ? String(prefill.email).trim()
        : '';
  if (!nameVal && !emailVal) return out;

  for (const slide of questions) {
    if (!Array.isArray(slide)) continue;
    const slideCode = slide[0]?.code;
    if (slideCode === undefined || slideCode === null) continue;

    for (const c of slide) {
      if (!c || c.type !== 'text' || !c.name || c.answerCode == null) continue;
      if (c.name === 'name' && nameVal) {
        out[slideCode] = {
          ...(out[slideCode] || {}),
          name: { value: nameVal, answerCode: c.answerCode },
        };
      }
      if (c.name === 'email' && emailVal) {
        out[slideCode] = {
          ...(out[slideCode] || {}),
          email: { value: emailVal, answerCode: c.answerCode },
        };
      }
    }
  }
  return out;
}

export const DEFAULT_QUIZ_COUNTRY_VALUE = 'Germany';
export function buildDefaultCountrySlideValues(questions) {
  const out = {};
  if (!Array.isArray(questions)) return out;
  for (const slide of questions) {
    if (!Array.isArray(slide) || slide.length === 0) continue;
    const slideCode = slide[0]?.code;
    const countryComp = slide.find(
      (c) => c && c.name === 'country' && c.type === 'select' && c.answerCode != null,
    );
    if (countryComp && slideCode !== undefined && slideCode !== null) {
      out[slideCode] = {
        country: { value: DEFAULT_QUIZ_COUNTRY_VALUE, answerCode: countryComp.answerCode },
      };
      break;
    }
  }
  return out;
}

export const normalizeKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const NORMALIZED_REASSURANCE = Object.fromEntries(
  Object.entries(MEDICAL_REASSURANCE).map(([key, entry]) => [normalizeKey(key), entry]),
);
